import { useForm } from '@folklore/forms';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import FormButton from '../buttons/FormButton';
import TextField from '../fields/TextField';
import FormStatus from '../partials/FormStatus';
import FormDescriptionTypography from '../typography/FormDescription';
import FormControl from './FormControl';

import styles from '../../styles/forms/verify-token-form.module.css';

interface VerifyTokenFormValues {
    token: string;
    [key: string]: unknown;
}

export interface VerifyTokenFormProps {
    postForm: (formAction: string, data: VerifyTokenFormValues) => Promise<unknown>;
    method?: 'email' | 'phone';
    autoFocus?: boolean;
    withoutDescription?: boolean;
    onComplete?: ((result: unknown) => void) | null;
    onClickCancel?: (() => void) | null;
    inputClassName?: string | null;
    className?: string | null;
}

function VerifyTokenForm({
    postForm,
    method = 'email',
    autoFocus = false,
    withoutDescription = false,
    onComplete = null,
    onClickCancel = null,
    inputClassName = null,
    className = null,
}: VerifyTokenFormProps) {
    const intl = useIntl();
    const formFields = ['token'];
    const inputRef = useRef<HTMLInputElement | null>(null);

    const { fields, onSubmit, status, errors } = useForm<unknown, VerifyTokenFormValues>({
        fields: formFields,
        postForm,
        onComplete,
    });

    useEffect(() => {
        if (autoFocus && inputRef.current !== null) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    return (
        <form
            onSubmit={onSubmit}
            className={classNames([
                styles.container,
                {
                    'was-validated': errors !== null,
                },
                className,
            ])}
        >
            {!withoutDescription ? (
                <FormDescriptionTypography className={styles.description}>
                    <FormattedMessage
                        defaultMessage="Un code de vérification vous a été envoyé par {method}."
                        description="Form description"
                        values={{
                            method: method === 'email' ? 'courriel' : 'SMS',
                        }}
                    />
                </FormDescriptionTypography>
            ) : null}
            <FormControl
                {...fields.token}
                withoutErrors
                label={
                    <FormattedMessage
                        defaultMessage="Entrez votre code de vérification ci-dessous :"
                        description="Form label"
                    />
                }
                className={styles.formControl}
            >
                <div className={styles.row}>
                    <div
                        className={classNames([
                            styles.field,
                            {
                                [styles.hasStatus]: status !== null,
                            },
                        ])}
                    >
                        <TextField
                            {...fields.token}
                            withoutAutocomplete
                            required
                            ref={inputRef}
                            placeholder={intl.formatMessage({
                                defaultMessage: 'Code',
                                description: 'Field placeholder',
                            })}
                            className={classNames([styles.input, inputClassName])}
                        />
                        <FormStatus status={status} className={styles.status} />
                    </div>
                    <div className={styles.buttons}>
                        <FormButton
                            type="submit"
                            disabled={status === 'loading'}
                            className={styles.button}
                        >
                            <FormattedMessage
                                defaultMessage="Vérifier"
                                description="Button label"
                            />
                        </FormButton>
                        <FormButton
                            onClick={onClickCancel}
                            transparent
                            compact
                            disabled={status === 'loading'}
                            className={styles.button}
                        >
                            <FormattedMessage defaultMessage="Annuler" description="Button label" />
                        </FormButton>
                    </div>
                </div>
            </FormControl>
        </form>
    );
}

export default VerifyTokenForm;
